import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";

import { HStack, VStack } from "@/components/layout";
import { cx } from "@/utils";

export default function FileDropzone({ files, setFiles, accept, maxSize }) {
  const onDrop = useCallback((files) => setFiles(files), []);
  const mbInBytes = 1000000;
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize: maxSize * mbInBytes,
  });

  const removeFile = (i) => {
    const value = [...files];
    value.splice(i, 1);
    setFiles(value);
  };

  return (
    <div>
      {files.length > 0 ? (
        <div
          data-slot="control"
          className={cx(
            // Basic layout
            "group relative block w-full",
            // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
            "before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-white before:shadow",
            // Focus ring
            "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent after:has-[[data-focus]]:ring-2 after:has-[[data-focus]]:ring-blue-500",
            // Disabled state
            "has-[[data-disabled]]:opacity-50 before:has-[[data-disabled]]:bg-zinc-950/5 before:has-[[data-disabled]]:shadow-none",
          )}
        >
          <div
            className={cx(
              "relative block w-full appearance-none rounded-md p-2",
              // Border
              "border border-zinc-950/10 data-[hover]:border-zinc-950/20",
              // Background color
              "bg-transparent",
              "max-h-96 overflow-auto",
            )}
          >
            {files.map(({ name }, i) => (
              <HStack
                key={`${name}-${i}`}
                className="items-center border-b border-zinc-950/10 py-2 text-zinc-600 first:pt-0 last:border-0 last:pb-0 data-[hover]:border-zinc-950/20"
              >
                <span className="bg-cp-primary-200/20 inline-flex rounded p-1">
                  <DocumentTextIcon
                    width={20}
                    className="fill-cp-primary-200"
                  />
                </span>
                <span className="flex-1">{name}</span>
                <button onClick={() => removeFile(i)} type="button">
                  <XMarkIcon width={20} className="fill-zinc-500" />
                </button>
              </HStack>
            ))}
          </div>
        </div>
      ) : (
        <div {...getRootProps()}>
          <VStack
            className={cx(
              isDragActive && "bg-cp-primary-200/10",
              "flex flex-col items-center rounded-md border border-dashed border-zinc-950/20 p-10",
            )}
          >
            <input {...getInputProps()} />
            <CloudArrowUpIcon width={40} className="fill-cp-primary-300" />
            <p>
              <button
                className="text-cp-primary-300 font-bold underline"
                type="button"
              >
                Click to upload
              </button>{" "}
              or drag and drop
            </p>
            {maxSize && (
              <p className="text-xs text-zinc-400">Max size {maxSize}MB</p>
            )}
          </VStack>
        </div>
      )}{" "}
    </div>
  );
}
